import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.editor');
/**
 * page.editor — 영상편집 페이지 (리팩토링)
 *
 * 타임라인·재생·스냅·루프·Undo/Redo 로직은
 * TimelineController (@wiz/libs/portal/editor-core/timeline) 에 위임한다.
 */
import { OnDestroy, OnInit } from '@angular/core';
import { Service } from "src/libs/portal/season/service";
import { HistoryManager }     from "src/libs/portal/editor-core/history";
import { TimelineController } from "src/libs/portal/editor-core/timeline";

@Component({
    selector: 'wiz-page-editor',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.editor/view.scss */
/* src/app/page.editor/view.scss: no such file or directory */`],
})
export class PageEditorComponent implements OnInit, OnDestroy {
    public loading: boolean = false;
    public saving: boolean = false;
    public dirty: boolean = false;

    public mediaPanelOpen: boolean = true;
    public rightPanelOpen: boolean = false;
    public timelineZoom: number = 1;
    public snapEnabled: boolean = true;
    public snapUnit: number = 0.5;
    public loopEnabled: boolean = false;
    public loopStart: number = 0;
    public loopEnd: number = 0;
    public currentTime: number = 0;
    public isPlaying: boolean = false;
    public isDraggingPlayhead: boolean = false;
    private playTimer: any = null;
    private dragTrackEl: HTMLElement | null = null;
    private ignoreNextSeekClick: boolean = false;
    private dragStartedAtX: number = 0;
    public clipName: string = 'sample-clip.mp4';
    public playbackSpeed: number = 100;
    public opacity: number = 100;
    public clipDuration: number = 8;
    public timelineClips: any[] = [];
    public selectedClipIndex: number = 0;
    public undoStack: any[] = [];
    public redoStack: any[] = [];
    public maxHistorySize: number = 50;
    public newClipName: string = '';
    public draggedMediaName: string = '';
    public mediaLibrary: any[] = [
        { name: 'sample-clip.mp4', duration: 8 },
        { name: 'intro-title.mov', duration: 5 },
        { name: 'interview-a001.mp4', duration: 12 },
        { name: 'bgm-track-01.mp3', duration: 30 },
    ];

    public get totalTimelineDuration(): number {
        return this.timelineClips.reduce((sum, clip) => sum + Number(clip.duration || 0), 0);
    }

    public get secondsPerPixel(): number {
        return 24 * this.timelineZoom;
    }

    public get timelineTrackWidthPx(): number {
        return Math.max(320, Math.round(this.totalTimelineDuration * this.secondsPerPixel));
    }

    public get playheadLeftPx(): number {
        return Math.max(0, Math.min(this.timelineTrackWidthPx, Math.round(this.currentTime * this.secondsPerPixel)));
    }

    public get selectedClipRangeLeftPx(): number {
        let sum = 0;
        for (let i = 0; i < this.selectedClipIndex; i++) {
            sum += Number(this.timelineClips[i]?.duration || 0);
        }
        return Math.round(sum * this.secondsPerPixel);
    }

    public get selectedClipRangeWidthPx(): number {
        if (this.selectedClipIndex < 0 || this.selectedClipIndex >= this.timelineClips.length) return 0;
        const duration = Number(this.timelineClips[this.selectedClipIndex]?.duration || 0);
        return Math.max(0, Math.round(duration * this.secondsPerPixel));
    }

    public get timelineZoomPercent(): number {
        return Math.round(this.timelineZoom * 100);
    }

    public get canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    public get canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    public get selectedClipLocked(): boolean {
        return this.isClipLocked(this.selectedClipIndex);
    }

    public get hasValidLoopRange(): boolean {
        return this.loopEnd - this.loopStart >= 0.1;
    }

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
        await this.service.auth.allow("/access");
        window.addEventListener('keydown', this.onWindowKeyDown);
        await this.load();
    }

    public ngOnDestroy() {
        window.removeEventListener('keydown', this.onWindowKeyDown);
        this.stopPlayback(false);
        this.detachPlayheadDragListeners();
    }

    private isTypingTarget(element: EventTarget | null): boolean {
        if (!(element instanceof HTMLElement)) return false;
        const tag = element.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
        if (element.isContentEditable) return true;
        return false;
    }

    private onWindowKeyDown = (event: KeyboardEvent) => {
        if (this.isTypingTarget(event.target)) return;

        const key = event.key.toLowerCase();
        const withModifier = event.ctrlKey || event.metaKey;

        if (key === ' ' || event.code === 'Space') {
            event.preventDefault();
            void this.togglePlayback();
            return;
        }

        if (key === 'delete' || key === 'backspace') {
            event.preventDefault();
            void this.removeClip(this.selectedClipIndex);
            return;
        }

        if (withModifier && key === 'z' && event.shiftKey === false) {
            event.preventDefault();
            void this.undo();
            return;
        }

        if (withModifier && ((key === 'z' && event.shiftKey) || key === 'y')) {
            event.preventDefault();
            void this.redo();
            return;
        }
    }

    public async load() {
        this.loading = true;
        await this.service.render();

        const { code, data } = await wiz.call("load");
        if (code === 200 && data) {
            this.mediaPanelOpen = data.mediaPanelOpen !== false;
            this.rightPanelOpen = !!data.rightPanelOpen;
            this.timelineZoom = Number(data.timelineZoom || 1);
            this.snapEnabled = data.snapEnabled !== false;
            this.loopEnabled = data.loopEnabled === true;
            this.loopStart = Number(data.loopStart || 0);
            this.loopEnd = Number(data.loopEnd || 0);
            this.currentTime = Number(data.playheadTime || 0);
            this.clipName = data.clipName || 'sample-clip.mp4';
            this.playbackSpeed = Number(data.playbackSpeed || 100);
            this.opacity = Number(data.opacity || 100);
            this.timelineClips = (data.timelineClips || []).map((clip: any) => this.normalizeClip(clip));
            this.selectedClipIndex = Number(data.selectedClipIndex || 0);

            if (this.timelineClips.length === 0) {
                this.timelineClips = [{
                    name: 'sample-clip.mp4',
                    playbackSpeed: 100,
                    opacity: 100,
                    duration: 8,
                    locked: false,
                }];
                this.selectedClipIndex = 0;
            }

            await this.selectClip(this.selectedClipIndex, false);
            this.dirty = false;
            this.undoStack = [];
            this.redoStack = [];
        }

        if (this.currentTime > this.totalTimelineDuration) {
            this.currentTime = this.totalTimelineDuration;
        }

        this.normalizeLoopRange();

        this.loading = false;
        await this.service.render();
    }

    private cloneTimelineClips() {
        return JSON.parse(JSON.stringify(this.timelineClips || []));
    }

    private normalizeClip(clip: any) {
        return {
            name: String(clip?.name || 'clip.mp4'),
            playbackSpeed: Number(clip?.playbackSpeed || 100),
            opacity: Number(clip?.opacity || 100),
            duration: Math.max(1, Number(clip?.duration || 1)),
            locked: !!clip?.locked,
        };
    }

    private isClipLocked(index: number): boolean {
        if (index < 0 || index >= this.timelineClips.length) return false;
        return !!this.timelineClips[index]?.locked;
    }

    public async toggleSelectedClipLock() {
        await this.toggleClipLock(this.selectedClipIndex);
    }

    public async toggleClipLock(index: number) {
        if (index < 0 || index >= this.timelineClips.length) return;

        this.pushUndoSnapshot();
        this.redoStack = [];

        this.timelineClips[index].locked = !this.timelineClips[index].locked;
        if (index === this.selectedClipIndex) {
            await this.selectClip(index, false);
        }

        this.dirty = true;
        await this.service.render();
    }

    private createSnapshot() {
        return {
            timelineClips: this.cloneTimelineClips(),
            selectedClipIndex: this.selectedClipIndex,
            currentTime: this.currentTime,
            loopEnabled: this.loopEnabled,
            loopStart: this.loopStart,
            loopEnd: this.loopEnd,
        };
    }

    private normalizeLoopRange() {
        const total = this.totalTimelineDuration;
        this.loopStart = Math.max(0, Math.min(total, Number(this.loopStart || 0)));
        this.loopEnd = Math.max(0, Math.min(total, Number(this.loopEnd || 0)));
        if (this.loopEnd < this.loopStart) {
            const tmp = this.loopStart;
            this.loopStart = this.loopEnd;
            this.loopEnd = tmp;
        }
        if (this.hasValidLoopRange === false) {
            this.loopEnabled = false;
        }
    }

    public async setLoopStartAtPlayhead() {
        this.loopStart = this.snapTime(this.currentTime);
        this.normalizeLoopRange();
        this.dirty = true;
        await this.service.render();
    }

    public async setLoopEndAtPlayhead() {
        this.loopEnd = this.snapTime(this.currentTime);
        this.normalizeLoopRange();
        this.dirty = true;
        await this.service.render();
    }

    public async toggleLoopPlayback() {
        if (this.hasValidLoopRange === false) {
            await this.service.modal.error('루프 구간이 올바르지 않습니다. A/B 포인트를 먼저 설정해주세요.');
            return;
        }
        this.loopEnabled = !this.loopEnabled;
        if (this.loopEnabled && (this.currentTime < this.loopStart || this.currentTime > this.loopEnd)) {
            this.currentTime = this.loopStart;
        }
        this.dirty = true;
        await this.service.render();
    }

    public async clearLoopRange() {
        this.loopEnabled = false;
        this.loopStart = 0;
        this.loopEnd = 0;
        this.dirty = true;
        await this.service.render();
    }

    private pushUndoSnapshot() {
        this.undoStack.push(this.createSnapshot());
        if (this.undoStack.length > this.maxHistorySize) {
            this.undoStack.shift();
        }
    }

    private async applySnapshot(snapshot: any) {
        this.timelineClips = Array.isArray(snapshot?.timelineClips) ? snapshot.timelineClips : [];
        this.timelineClips = this.timelineClips.map((clip: any) => this.normalizeClip(clip));
        if (this.timelineClips.length === 0) {
            this.timelineClips = [{
                name: 'sample-clip.mp4',
                playbackSpeed: 100,
                opacity: 100,
                duration: 8,
                locked: false,
            }];
        }

        let index = Number(snapshot?.selectedClipIndex ?? 0);
        if (index < 0) index = 0;
        if (index >= this.timelineClips.length) index = this.timelineClips.length - 1;
        this.selectedClipIndex = index;

        const total = this.totalTimelineDuration;
        const snapTime = Number(snapshot?.currentTime ?? 0);
        this.currentTime = Math.max(0, Math.min(total, snapTime));
        this.loopEnabled = snapshot?.loopEnabled === true;
        this.loopStart = Number(snapshot?.loopStart ?? this.loopStart);
        this.loopEnd = Number(snapshot?.loopEnd ?? this.loopEnd);
        this.normalizeLoopRange();

        await this.selectClip(this.selectedClipIndex, false);
    }

    public async undo() {
        if (this.undoStack.length === 0) return;

        const current = this.createSnapshot();
        const snapshot = this.undoStack.pop();
        this.redoStack.push(current);

        await this.applySnapshot(snapshot);
        this.dirty = true;
        await this.service.render();
    }

    public async redo() {
        if (this.redoStack.length === 0) return;

        const current = this.createSnapshot();
        const snapshot = this.redoStack.pop();
        this.undoStack.push(current);

        await this.applySnapshot(snapshot);
        this.dirty = true;
        await this.service.render();
    }

    public async toggleRightPanel() {
        this.rightPanelOpen = !this.rightPanelOpen;
        this.dirty = true;
        await this.service.render();
    }

    public async toggleMediaPanel() {
        this.mediaPanelOpen = !this.mediaPanelOpen;
        this.dirty = true;
        await this.service.render();
    }

    public async zoomInTimeline() {
        this.timelineZoom = Math.min(3, Number((this.timelineZoom + 0.1).toFixed(2)));
        this.dirty = true;
        await this.service.render();
    }

    public async zoomOutTimeline() {
        this.timelineZoom = Math.max(0.5, Number((this.timelineZoom - 0.1).toFixed(2)));
        this.dirty = true;
        await this.service.render();
    }

    public async resetTimelineZoom() {
        this.timelineZoom = 1;
        this.dirty = true;
        await this.service.render();
    }

    public snapTime(time: number): number {
        const safeTime = Number.isFinite(time) ? time : 0;
        const unit = Math.max(0.1, Number(this.snapUnit || 0.5));
        const snapped = this.snapEnabled ? Math.round(safeTime / unit) * unit : safeTime;
        return Number(snapped.toFixed(2));
    }

    public async toggleSnap() {
        this.snapEnabled = !this.snapEnabled;
        this.currentTime = this.snapTime(this.currentTime);
        await this.syncSelectedClipWithCurrentTime();
        this.dirty = true;
        await this.service.render();
    }

    public async togglePlayback() {
        if (this.isPlaying) {
            this.stopPlayback();
            return;
        }

        this.isPlaying = true;
        const stepMs = 100;
        this.playTimer = setInterval(async () => {
            if (this.isPlaying === false) return;

            const step = (stepMs / 1000) * (this.playbackSpeed / 100);
            this.currentTime = Number((this.currentTime + step).toFixed(2));

            if (this.loopEnabled && this.hasValidLoopRange) {
                if (this.currentTime > this.loopEnd) {
                    this.currentTime = this.loopStart;
                }
            }

            if (this.currentTime >= this.totalTimelineDuration) {
                this.currentTime = this.totalTimelineDuration;
                this.stopPlayback(false);
            }

            await this.service.render();
        }, stepMs);

        await this.service.render();
    }

    public stopPlayback(markDirty: boolean = true) {
        this.isPlaying = false;
        if (this.playTimer) {
            clearInterval(this.playTimer);
            this.playTimer = null;
        }
        if (markDirty) {
            this.dirty = true;
        }
    }

    public async resetPlayhead() {
        this.stopPlayback(false);
        this.currentTime = 0;
        this.dirty = true;
        await this.service.render();
    }

    public clipWidthPx(clip: any): number {
        const duration = Math.max(1, Number(clip?.duration || 1));
        return Math.max(56, Math.round(duration * 24 * this.timelineZoom));
    }

    public clipStartTime(index: number): number {
        let sum = 0;
        for (let i = 0; i < index; i++) {
            sum += Number(this.timelineClips[i]?.duration || 0);
        }
        return sum;
    }

    public async splitSelectedClipAtPlayhead() {
        if (this.selectedClipIndex < 0 || this.selectedClipIndex >= this.timelineClips.length) {
            await this.service.modal.error("분할할 클립이 선택되지 않았습니다.");
            return;
        }

        if (this.isClipLocked(this.selectedClipIndex)) {
            await this.service.modal.error("잠긴 클립은 분할할 수 없습니다.");
            return;
        }

        this.pushUndoSnapshot();
        this.redoStack = [];

        const clip = this.timelineClips[this.selectedClipIndex];
        const duration = Math.max(1, Number(clip?.duration || 1));
        const start = this.clipStartTime(this.selectedClipIndex);
        const offset = this.currentTime - start;

        if (offset <= 1 || offset >= duration - 1) {
            await this.service.modal.error("플레이헤드를 클립 내부(양 끝 제외)로 이동한 뒤 분할해주세요.");
            return;
        }

        const leftDuration = Math.max(1, Math.floor(offset));
        const rightDuration = Math.max(1, duration - leftDuration);

        const originalName = String(clip?.name || "clip");
        const dotIndex = originalName.lastIndexOf('.');
        const hasExt = dotIndex > 0;
        const stem = hasExt ? originalName.substring(0, dotIndex) : originalName;
        const ext = hasExt ? originalName.substring(dotIndex) : "";

        const leftClip = {
            ...clip,
            name: `${stem}_A${ext}`,
            duration: leftDuration,
        };
        const rightClip = {
            ...clip,
            name: `${stem}_B${ext}`,
            duration: rightDuration,
        };

        this.timelineClips.splice(this.selectedClipIndex, 1, leftClip, rightClip);
        this.normalizeLoopRange();

        this.selectedClipIndex = this.selectedClipIndex + 1;
        this.currentTime = start + leftDuration;

        await this.selectClip(this.selectedClipIndex, false);
        this.dirty = true;
        await this.service.render();
    }

    public async duplicateSelectedClip() {
        if (this.selectedClipIndex < 0 || this.selectedClipIndex >= this.timelineClips.length) {
            await this.service.modal.error("복제할 클립이 선택되지 않았습니다.");
            return;
        }

        this.pushUndoSnapshot();
        this.redoStack = [];

        const source = this.timelineClips[this.selectedClipIndex];
        const sourceName = String(source?.name || "clip");
        const dotIndex = sourceName.lastIndexOf('.');
        const hasExt = dotIndex > 0;
        const stem = hasExt ? sourceName.substring(0, dotIndex) : sourceName;
        const ext = hasExt ? sourceName.substring(dotIndex) : "";

        const duplicated = {
            ...source,
            name: `${stem}_copy${ext}`,
        };

        const insertIndex = this.selectedClipIndex + 1;
        this.timelineClips.splice(insertIndex, 0, duplicated);
        this.normalizeLoopRange();

        await this.selectClip(insertIndex, false);
        this.dirty = true;
        await this.service.render();
    }

    public async seekTimeline(event: MouseEvent) {
        if (this.ignoreNextSeekClick) {
            this.ignoreNextSeekClick = false;
            return;
        }

        const target = event.currentTarget as HTMLElement;
        if (!target) return;

        const rect = target.getBoundingClientRect();
        const relativeX = event.clientX - rect.left + target.scrollLeft;
        const time = relativeX / this.secondsPerPixel;

        this.currentTime = Math.max(0, Math.min(this.totalTimelineDuration, this.snapTime(time)));
        await this.syncSelectedClipWithCurrentTime();
        this.dirty = true;
        await this.service.render();
    }

    private updatePlayheadFromClientX(clientX: number) {
        if (!this.dragTrackEl) return;

        const rect = this.dragTrackEl.getBoundingClientRect();
        const relativeX = clientX - rect.left + this.dragTrackEl.scrollLeft;
        const time = relativeX / this.secondsPerPixel;
        this.currentTime = Math.max(0, Math.min(this.totalTimelineDuration, this.snapTime(time)));
    }

    private onWindowMouseMove = (event: MouseEvent) => {
        if (this.isDraggingPlayhead === false) return;
        this.updatePlayheadFromClientX(event.clientX);
        void this.service.render();
    }

    private onWindowMouseUp = () => {
        if (this.isDraggingPlayhead === false) return;

        const moved = Math.abs(this.playheadLeftPx - this.dragStartedAtX) > 2;
        this.isDraggingPlayhead = false;
        this.detachPlayheadDragListeners();
        this.dragTrackEl = null;

        if (moved) {
            this.ignoreNextSeekClick = true;
        }

        void (async () => {
            await this.syncSelectedClipWithCurrentTime();
            this.dirty = true;
            await this.service.render();
        })();
    }

    private attachPlayheadDragListeners() {
        window.addEventListener('mousemove', this.onWindowMouseMove);
        window.addEventListener('mouseup', this.onWindowMouseUp);
    }

    private detachPlayheadDragListeners() {
        window.removeEventListener('mousemove', this.onWindowMouseMove);
        window.removeEventListener('mouseup', this.onWindowMouseUp);
    }

    public startPlayheadDrag(event: MouseEvent, trackEl: HTMLElement) {
        event.preventDefault();
        event.stopPropagation();

        this.isDraggingPlayhead = true;
        this.dragTrackEl = trackEl;
        this.dragStartedAtX = this.playheadLeftPx;
        this.updatePlayheadFromClientX(event.clientX);
        this.attachPlayheadDragListeners();

        void this.service.render();
    }

    public async syncSelectedClipWithCurrentTime() {
        let cursor = 0;
        for (let i = 0; i < this.timelineClips.length; i++) {
            const duration = Number(this.timelineClips[i]?.duration || 0);
            const end = cursor + duration;
            if (this.currentTime >= cursor && this.currentTime <= end) {
                await this.selectClip(i, false);
                return;
            }
            cursor = end;
        }
    }

    public async onSettingChanged() {
        if (this.isClipLocked(this.selectedClipIndex)) {
            await this.service.modal.error("잠긴 클립은 속성을 변경할 수 없습니다.");
            await this.selectClip(this.selectedClipIndex, false);
            return;
        }

        this.pushUndoSnapshot();
        this.redoStack = [];

        if (this.timelineClips.length > 0 && this.selectedClipIndex >= 0 && this.selectedClipIndex < this.timelineClips.length) {
            this.timelineClips[this.selectedClipIndex].name = this.clipName;
            this.timelineClips[this.selectedClipIndex].playbackSpeed = this.playbackSpeed;
            this.timelineClips[this.selectedClipIndex].opacity = this.opacity;
            this.timelineClips[this.selectedClipIndex].duration = Math.max(1, Number(this.clipDuration || 1));
        }

        this.normalizeLoopRange();

        this.dirty = true;
        await this.service.render();
    }

    public async addClip() {
        this.pushUndoSnapshot();
        this.redoStack = [];

        const name = (this.newClipName || '').trim();
        const clipName = name === '' ? `clip-${this.timelineClips.length + 1}.mp4` : name;

        this.timelineClips.push({
            name: clipName,
            playbackSpeed: 100,
            opacity: 100,
            duration: 8,
            locked: false,
        });
        this.normalizeLoopRange();
        this.newClipName = '';
        await this.selectClip(this.timelineClips.length - 1, false);
        this.dirty = true;
        await this.service.render();
    }

    public async addClipFromMedia(mediaName: string, duration: number = 8) {
        this.pushUndoSnapshot();
        this.redoStack = [];

        this.timelineClips.push({
            name: mediaName,
            playbackSpeed: 100,
            opacity: 100,
            duration: duration,
            locked: false,
        });
        this.normalizeLoopRange();
        await this.selectClip(this.timelineClips.length - 1, false);
        this.dirty = true;
        await this.service.render();
    }

    public onMediaDragStart(mediaName: string) {
        this.draggedMediaName = mediaName;
    }

    public onTimelineDragOver(event: DragEvent) {
        event.preventDefault();
    }

    public async onTimelineDrop(event: DragEvent) {
        event.preventDefault();
        const mediaName = this.draggedMediaName;
        if (!mediaName) return;

        const media = this.mediaLibrary.find(item => item.name === mediaName);
        await this.addClipFromMedia(mediaName, media?.duration || 8);
        this.draggedMediaName = '';
    }

    public async removeClip(index: number) {
        if (index < 0 || index >= this.timelineClips.length) return;

        if (this.isClipLocked(index)) {
            await this.service.modal.error("잠긴 클립은 삭제할 수 없습니다.");
            return;
        }

        this.pushUndoSnapshot();
        this.redoStack = [];

        this.timelineClips.splice(index, 1);

        if (this.timelineClips.length === 0) {
            this.timelineClips.push({
                name: 'sample-clip.mp4',
                playbackSpeed: 100,
                opacity: 100,
                duration: 8,
                locked: false,
            });
            this.selectedClipIndex = 0;
        } else if (this.selectedClipIndex >= this.timelineClips.length) {
            this.selectedClipIndex = this.timelineClips.length - 1;
        }

        this.normalizeLoopRange();

        await this.selectClip(this.selectedClipIndex, false);
        this.dirty = true;
        await this.service.render();
    }

    public async moveClipLeft(index: number) {
        if (index <= 0 || index >= this.timelineClips.length) return;

        if (this.isClipLocked(index) || this.isClipLocked(index - 1)) {
            await this.service.modal.error("잠긴 클립은 이동할 수 없습니다.");
            return;
        }

        this.pushUndoSnapshot();
        this.redoStack = [];

        const current = this.timelineClips[index];
        const prev = this.timelineClips[index - 1];
        this.timelineClips[index - 1] = current;
        this.timelineClips[index] = prev;

        if (this.selectedClipIndex === index) {
            this.selectedClipIndex = index - 1;
        } else if (this.selectedClipIndex === index - 1) {
            this.selectedClipIndex = index;
        }

        await this.selectClip(this.selectedClipIndex, false);
        this.dirty = true;
        await this.service.render();
    }

    public async moveClipRight(index: number) {
        if (index < 0 || index >= this.timelineClips.length - 1) return;

        if (this.isClipLocked(index) || this.isClipLocked(index + 1)) {
            await this.service.modal.error("잠긴 클립은 이동할 수 없습니다.");
            return;
        }

        this.pushUndoSnapshot();
        this.redoStack = [];

        const current = this.timelineClips[index];
        const next = this.timelineClips[index + 1];
        this.timelineClips[index + 1] = current;
        this.timelineClips[index] = next;

        if (this.selectedClipIndex === index) {
            this.selectedClipIndex = index + 1;
        } else if (this.selectedClipIndex === index + 1) {
            this.selectedClipIndex = index;
        }

        await this.selectClip(this.selectedClipIndex, false);
        this.dirty = true;
        await this.service.render();
    }

    public async selectClip(index: number, markDirty: boolean = true) {
        if (index < 0 || index >= this.timelineClips.length) return;

        this.selectedClipIndex = index;
        const clip = this.timelineClips[index];
        this.clipName = clip.name || 'clip.mp4';
        this.playbackSpeed = Number(clip.playbackSpeed || 100);
        this.opacity = Number(clip.opacity || 100);
        this.clipDuration = Math.max(1, Number(clip.duration || 1));

        if (markDirty) {
            this.dirty = true;
        }
        await this.service.render();
    }

    public async saveProject() {
        this.saving = true;
        await this.service.render();

        this.stopPlayback(false);

        const { code, data } = await wiz.call("save", {
            state: JSON.stringify({
                mediaPanelOpen: this.mediaPanelOpen,
                rightPanelOpen: this.rightPanelOpen,
                timelineZoom: this.timelineZoom,
                snapEnabled: this.snapEnabled,
                loopEnabled: this.loopEnabled,
                loopStart: this.loopStart,
                loopEnd: this.loopEnd,
                playheadTime: this.currentTime,
                clipName: this.clipName,
                playbackSpeed: this.playbackSpeed,
                opacity: this.opacity,
                timelineClips: this.timelineClips,
                selectedClipIndex: this.selectedClipIndex,
            })
        });

        this.saving = false;

        if (code === 200) {
            this.dirty = false;
            await this.service.modal.success(`프로젝트가 저장되었습니다. (${data.updated})`);
        } else {
            await this.service.modal.error(data || "저장에 실패했습니다.");
        }

        await this.service.render();
    }
}

export default PageEditorComponent;